import {
  deleteMultipartUploadFileApi,
  getUncompletedFileInfosApi,
  stopWellsDataImportTaskApi,
} from '@core/core/api'
import type {
  ConvertProgressNotificationInfo,
  LastUploadPartInfo,
  MarkerSetupType,
  MultipartFileStatusPerStepType,
  MultipartFileStatusType,
  MultipartUploadedFile,
  MultipartUploadedFileSetupType,
  SegySetupType,
  UploadedFileEntityType,
  UploadedFileStepTransitionType,
  UploadedFileStepType,
  UploadState,
  UploadStep,
} from '@core/core/types'
import { defaultUploadedFileStepStatuses } from '@core/core/types'
import type { ProjectId } from 'common/types'

import { updateGeoDbFoundEntities } from '../../overallSearch'
import type { AppThunk } from '../../store'
import { isMultipartSetupOfSpecificType } from '../helpers'
import {
  selectById,
  selectMultipartFileByTaskId,
  selectMultipartFileByUploadId,
  selectMultipartUploadAll,
} from '../multipartUploadedFileSelectors'
import { multipartUploadedFileSlice } from '../multipartUploadedFileSlice'
import {
  addMarkerMultipartsToProject,
  updateMarkerSetupConfig,
  updateMarkerUploadedFileStep,
} from './markers'
import {
  addSegyMultipartsToProject,
  addSegyUncompletedFiles,
  updateSegyReadyState,
  updateSegySetupConfig,
  updateSegyUploadedFileStep,
} from './segy'

export const DefaultChunkSize = 100_000_000

export const {
  addMultipartFiles,
  setActiveMultipartFile,
  updateMultipartState,
  updateMultipartContext,
  removeMultipartFiles,
} = multipartUploadedFileSlice.actions
const { clearMultipartFiles } = multipartUploadedFileSlice.actions

export const updateUploadedFileStep =
  (
    projectId: ProjectId,
    fileId: string,
    step: UploadedFileStepTransitionType,
    file: File,
  ): AppThunk =>
  async (dispatch, getState) => {
    const state = getState()
    const entity = selectById(state, fileId)
    if (!entity) throw new Error('entity не найден')

    if (entity.entityType === 'segy') {
      await updateSegyUploadedFileStep(projectId, fileId, step, file, entity, dispatch, state)
    } else if (entity.entityType === 'marker') {
      updateMarkerUploadedFileStep(projectId, fileId, step, file, entity, dispatch, state)
    }
  }

export const tryAddUploadableFiles =
  (files: LastUploadPartInfo[]): AppThunk =>
  async (dispatch, getState) => {
    const oldFiles = selectMultipartUploadAll(getState())
    const newFiles = files.filter(file =>
      oldFiles.every(
        oldFile => oldFile.fileSize !== file.fileSize || oldFile.name !== file.fileName,
      ),
    )
    if (newFiles.length) {
      newFiles.forEach(file => {
        file.step = 'Uploading'
        file.state = 'InProgress'
      })
      await addSegyUncompletedFiles(dispatch, files)
    }
  }

export const tryUpdateUploadFileState =
  (data: { entityId?: string; taskId?: string; step: UploadStep; state: UploadState }): AppThunk =>
  (dispatch, getState) => {
    let entity: MultipartUploadedFile | undefined = undefined
    const state = getState()
    if (data.entityId) {
      entity = selectMultipartFileByUploadId(state, data.entityId)
    } else if (data.taskId) {
      entity = selectMultipartFileByTaskId(state, data.taskId)
    }
    if (!entity?.uploadId) return

    const fileId = entity.fileId
    try {
      if (
        entity.origin === 'geodb' &&
        entity.step === 'loading' &&
        (data.state === 'RanToCompletion' || data.state === 'Faulted')
      ) {
        dispatch(
          updateMultipartState({
            id: entity.fileId,
            changes: {
              statuses:
                data.state !== 'RanToCompletion'
                  ? { loading: 'failed' }
                  : { loading: 'ready', ready: 'ready' },
            },
          }),
        )
        dispatch(removeMultipartFiles([entity.fileId]))
        dispatch(updateGeoDbFoundEntities([entity.uploadId], 'addedToProject'))
      } else if (
        entity.origin !== 'geodb' &&
        entity.step === 'loading' &&
        data.step === 'Converting'
      ) {
        const statuses = stepsToStatusesMapping.get('loading->conversion')
        dispatch(
          updateMultipartState({
            id: fileId,
            changes: {
              progress: 100,
              step: 'conversion',
              statuses: { ...entity.statuses, ...statuses },
            },
          }),
        )
      }
    } catch (error) {
      dispatch(
        updateMultipartState({
          id: fileId,
          changes: {
            statuses: { ...entity.statuses, loading: 'failed' },
            error: error?.toString(),
          },
        }),
      )
    }
  }

export const tryRemoveUploadableFiles =
  (data: { uploadId: string; projectId: ProjectId }[]): AppThunk =>
  (dispatch, getState) => {
    data.forEach(file => {
      const entity = selectMultipartFileByUploadId(getState(), file.uploadId)
      if (!entity?.uploadId) return

      dispatch(removeMultipartFiles([entity.fileId]))
    })
  }

export const initMultipartFiles =
  (projectId: ProjectId): AppThunk =>
  async dispatch => {
    dispatch(clearMultipartFiles())

    const files = await getUncompletedFileInfosApi(projectId)
    if (!files.length) return

    await addSegyUncompletedFiles(dispatch, files)
  }

export const updateSetupConfig =
  (fileId: string, setupContent: MultipartUploadedFileSetupType | undefined): AppThunk =>
  (dispatch, getState) => {
    const entity = selectById(getState(), fileId)
    if (
      entity?.entityType === 'segy' &&
      isMultipartSetupOfSpecificType<SegySetupType>(entity, setupContent, 'segy')
    ) {
      updateSegySetupConfig(dispatch, fileId, setupContent, entity)
    } else if (
      entity?.entityType === 'marker' &&
      isMultipartSetupOfSpecificType<MarkerSetupType>(entity, setupContent, 'marker')
    ) {
      updateMarkerSetupConfig(dispatch, fileId, setupContent)
    }
  }

export const updateMultipartReadyState =
  (fileId: string): AppThunk =>
  (dispatch, getState) => {
    const entity = selectById(getState(), fileId)
    if (!entity?.uploadId) return

    try {
      if (entity.entityType === 'segy') {
        updateSegyReadyState(dispatch, fileId, entity)
      }
    } catch (error) {
      console.log(error)
      dispatch(
        updateMultipartState({
          id: fileId,
          changes: {
            statuses: { ...entity.statuses, ready: 'failed' },
            error: error?.toString(),
          },
        }),
      )
    }
  }

export const addMultipartsToProject =
  (projectId: ProjectId, fileIds: string[]): AppThunk =>
  (dispatch, getState) => {
    fileIds.map(async fileId => {
      const entity = selectById(getState(), fileId)
      if (!entity) return

      switch (entity.entityType) {
        case 'segy': {
          await addSegyMultipartsToProject(projectId, fileId, dispatch, entity)
          break
        }
        case 'marker': {
          addMarkerMultipartsToProject(projectId, fileId, dispatch, entity)
          break
        }
        default: {
          return
        }
      }
    })
  }

export const deleteMultipartsToProject =
  (fileIds: string[]): AppThunk =>
  (dispatch, getState) => {
    fileIds.map(async fileId => {
      const entity = selectById(getState(), fileId)
      if (entity?.uploadId && entity.entityType === 'segy') {
        await deleteMultipartUploadFileApi(entity.uploadId)
      }
      dispatch(removeMultipartFiles([fileId]))
    })
  }

export const tryCancelUploadingFiles =
  (tasksIds?: string[]): AppThunk =>
  (dispatch, getState) => {
    tasksIds?.map(async taskId => {
      const entity = selectMultipartFileByTaskId(getState(), taskId)
      await stopWellsDataImportTaskApi(taskId)
      if (entity?.uploadId) {
        dispatch(updateGeoDbFoundEntities([entity.uploadId], 'notstarted'))
      }
    })
  }

export const stepsToStatusesMapping = new Map<
  `${UploadedFileStepType}->${UploadedFileStepType}`,
  MultipartFileStatusPerStepType
>([
  ['setup->mapping', { mapping: 'inprogress', setup: 'succeeded' }],
  ['mapping->setup', { setup: 'inprogress', mapping: 'notstarted' }],
  ['mapping->loading', { loading: 'inprogress', mapping: 'succeeded' }],
  ['setup->loading', { loading: 'inprogress', setup: 'succeeded' }],
  ['loading->setup', { setup: 'inprogress' }],
  ['setup->conversion', { conversion: 'inprogress', setup: 'succeeded' }],
  ['conversion->setup', { conversion: 'notstarted', setup: 'inprogress' }],
  ['loading->conversion', { loading: 'succeeded', conversion: 'inprogress' }],
  ['conversion->ready', { setup: 'ready', loading: 'ready', conversion: 'ready', ready: 'ready' }],
  ['loading->ready', { loading: 'ready', ready: 'ready' }],
])

export const transitionToStepsMapping = new Map<
  `${UploadedFileStepType}->${UploadedFileStepTransitionType}`,
  (
    statuses: MultipartFileStatusPerStepType,
    entityType: UploadedFileEntityType,
  ) => UploadedFileStepType
>([
  [
    'setup->next',
    statuses => {
      if (statuses.mapping !== 'excluded') {
        return 'mapping'
      }
      if (statuses.loading !== 'succeeded' && statuses.loading !== 'ready') {
        return 'loading'
      } else if (statuses.conversion !== 'succeeded' && statuses.conversion !== 'ready') {
        return 'conversion'
      }
      return 'ready'
    },
  ],
  ['mapping->prev', () => 'setup'],
  ['conversion->prev', statuses => (statuses.loading === 'succeeded' ? 'setup' : 'loading')],
  ['loading->prev', () => 'setup'],
  ['ready->prev', () => 'setup'],
  ['loading->next', statuses => (statuses.conversion === 'excluded' ? 'ready' : 'conversion')],
])

export const convertStepStatuses: (
  apiStep: UploadStep,
  apiState: UploadState,
) => { step: UploadedFileStepType; statuses: MultipartFileStatusPerStepType } = (
  apiStep,
  apiState,
) => {
  let step = apiStep
  if (apiStep === 'Converting' && apiState === 'Completed') {
    step = 'Ready'
  }

  const getLoadingState: () => MultipartFileStatusType = () => {
    switch (apiState) {
      case 'Failed':
        return 'failed'
      case 'InProgress':
        return 'paused'
      default:
        return 'paused'
    }
  }

  switch (step) {
    case 'Uploading': {
      return {
        step: 'loading',
        statuses: {
          ...defaultUploadedFileStepStatuses,
          setup: 'succeeded',
          loading: getLoadingState(),
          conversion: 'notstarted',
          ready: 'notstarted',
        },
      }
    }
    case 'Converting': {
      return {
        step: 'conversion',
        statuses: {
          ...defaultUploadedFileStepStatuses,
          setup: 'succeeded',
          loading: 'succeeded',
          conversion: apiState === 'Failed' ? 'failed' : 'notstarted',
          ready: 'notstarted',
        },
      }
    }
    case 'Ready': {
      return {
        step: 'ready',
        statuses: {
          ...defaultUploadedFileStepStatuses,
          setup: 'ready',
          loading: 'ready',
          conversion: 'ready',
          ready: 'ready',
        },
      }
    }
  }
}

export function getStatus(
  status: ConvertProgressNotificationInfo['status'],
): MultipartFileStatusPerStepType | undefined {
  switch (status) {
    case 'Done':
      return stepsToStatusesMapping.get('conversion->ready')
    case 'Failed':
      return { conversion: 'failed' }
    case 'Progress':
      return { conversion: 'inprogress' }
    case 'Pending':
    case 'Running':
      break
  }
}
